import { AddressSharedService, getEnableNewStatusMatchApi, ZipCode } from '@msc-b2c/shared-utils';
import { ZipCodeResponse } from '@msc-b2c/interfaces/interfaces';
import { validateSelect, validateSelectCustom } from '../select/select';
import { vrValidZipcodeAddress } from '../../Validations/validationRules';

const STATUS_MATCH_ADDRESS_VALIDATION = 'vr-status-match-zipcode-valid-addresses';
class InputPostcode {
  private inputPostcode: HTMLInputElement;
  private formContainer: HTMLFormElement;

  constructor(inputPostcode: HTMLInputElement) {
    this.inputPostcode = inputPostcode;
    this.formContainer = this.inputPostcode?.closest('.cmp-form') as HTMLFormElement;
  }

  init() {
    const postCodeButton = this.inputPostcode.nextElementSibling as HTMLButtonElement;
    let provincePlaceholder: HTMLOptionElement;
    let townPlaceholder: HTMLOptionElement;
    let countyPlaceholder: HTMLOptionElement;
    let countryCodePlaceholder: HTMLOptionElement;
    let provinceCustomPlaceholder: string;
    let townCustomPlaceholder: string;
    let countyCustomPlaceholder: string;
    let countryCodeCustomPlaceholder: string;

    // native province, town, region selects and countryCode
    const provinceSelect = this.formContainer?.querySelector(
      `select[name="customer.address.province"]`
    ) as HTMLSelectElement;
    if (provinceSelect) {
      provincePlaceholder = provinceSelect?.options[0];
    }
    const townSelect = this.formContainer?.querySelector(
      `select[name="customer.address.town"]`
    ) as HTMLSelectElement;
    if (townSelect) {
      townPlaceholder = townSelect?.options[0];
    }
    const countySelect = this.formContainer?.querySelector(
      `select[name="customer.address.region"]`
    ) as HTMLSelectElement;
    if (countySelect) {
      countyPlaceholder = countySelect?.options[0];
    }
    const countryCodeSelect = this.formContainer?.querySelector(
      `select[name="customer.address.countryCode"]`
    ) as HTMLSelectElement;
    if (countryCodeSelect) {
      countryCodePlaceholder = countryCodeSelect?.options[0];
    }

    // custom province, town and region selects
    const provinceCustomSelect = this.formContainer?.querySelector(
      `.custom-select[name="customer.address.province"]`
    ) as HTMLElement;
    if (provinceCustomSelect) {
      provinceCustomPlaceholder = provinceCustomSelect?.querySelector('.selected-value')
        ?.textContent as string;
    }
    const townCustomSelect = this.formContainer?.querySelector(
      `.custom-select[name="customer.address.town"]`
    ) as HTMLElement;
    if (townCustomSelect) {
      townCustomPlaceholder = townCustomSelect?.querySelector('.selected-value')
        ?.textContent as string;
    }
    const countyCustomSelect = this.formContainer?.querySelector(
      `.custom-select[name="customer.address.region"]`
    ) as HTMLElement;
    if (countyCustomSelect) {
      countyCustomPlaceholder = countyCustomSelect?.querySelector('.selected-value')
        ?.textContent as string;
    }
    const countryCodeCustomSelect = this.formContainer?.querySelector(
      `.custom-select[name="customer.address.countryCode"]`
    ) as HTMLElement;
    if (countryCodeSelect) {
      countryCodeCustomPlaceholder = countryCodeCustomSelect?.querySelector('.selected-value')
        ?.textContent as string;
    }

    postCodeButton?.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopImmediatePropagation();
          postCodeButton.click();
        }
      },
      true
    );

    postCodeButton?.addEventListener('click', (e) => {
      e.preventDefault();

      const isEnableTheNewStatusMatchApi = getEnableNewStatusMatchApi();
      const countryCodeSelectValue = countryCodeSelect?.value.trim() ?? '';
      const paramValue = this.inputPostcode.value;
      postCodeButton!.disabled = true;
      const zipCodeApi = new ZipCode(false, {
        postCode: paramValue,
      });

      if (paramValue != '') {
        if (isEnableTheNewStatusMatchApi) {
          const zipCodeService = new AddressSharedService(false, {
            countryCode: countryCodeSelectValue?.trim() ?? '',
            postCode: paramValue.trim(),
          });
          zipCodeService
            .getData()
            .then((res) => {
              const response = res as unknown as ZipCodeResponse;
              const hasInvalidTown = response.addresses.some((address) => !address.town?.trim());

              if (hasInvalidTown || response.addresses.length === 0) {
                // If no towns are retrieved, then the zipcode is not valid
                validateZipCodeAddress(this.inputPostcode);
                throw new Error();
              }

              if (response?.addresses) {
                // Province replace with state for US
                response.addresses.forEach((address) => {
                  if (!address.province) {
                    address.province = address.state || address.town;
                  }
                });

                handleConsistencyResult(
                  'province',
                  response.addresses,
                  provinceSelect,
                  provinceCustomSelect,
                  provincePlaceholder,
                  provinceCustomPlaceholder
                );

                // Town
                handleConsistencyResult(
                  'town',
                  response.addresses,
                  townSelect,
                  townCustomSelect,
                  townPlaceholder,
                  townCustomPlaceholder
                );

                // County (Region)
                handleConsistencyResult(
                  'region',
                  response.addresses,
                  countySelect,
                  countyCustomSelect,
                  countyPlaceholder,
                  countyCustomPlaceholder
                );
              }
              postCodeButton!.disabled = false;
            })
            .catch(() => {
              handleErrorResult(
                provinceSelect,
                provinceCustomSelect,
                provincePlaceholder,
                provinceCustomPlaceholder,
                postCodeButton
              );
              handleErrorResult(
                townSelect,
                townCustomSelect,
                townPlaceholder,
                townCustomPlaceholder,
                postCodeButton
              );
              handleErrorResult(
                countySelect,
                countyCustomSelect,
                countyPlaceholder,
                countyCustomPlaceholder,
                postCodeButton
              );
            });
        } else {
          zipCodeApi
            .getData()
            .then((res) => {
              const response = res as unknown as ZipCodeResponse;
              if (response?.addresses) {
                // Province
                handleConsistencyResult(
                  'province',
                  response.addresses,
                  provinceSelect,
                  provinceCustomSelect,
                  provincePlaceholder,
                  provinceCustomPlaceholder
                );

                // Town
                handleConsistencyResult(
                  'town',
                  response.addresses,
                  townSelect,
                  townCustomSelect,
                  townPlaceholder,
                  townCustomPlaceholder
                );

                // County (Region)
                handleConsistencyResult(
                  'region',
                  response.addresses,
                  countySelect,
                  countyCustomSelect,
                  countyPlaceholder,
                  countyCustomPlaceholder
                );
              }
              postCodeButton!.disabled = false;
            })
            .catch(() => {
              handleErrorResult(
                provinceSelect,
                provinceCustomSelect,
                provincePlaceholder,
                provinceCustomPlaceholder,
                postCodeButton
              );
              handleErrorResult(
                townSelect,
                townCustomSelect,
                townPlaceholder,
                townCustomPlaceholder,
                postCodeButton
              );
              handleErrorResult(
                countySelect,
                countyCustomSelect,
                countyPlaceholder,
                countyCustomPlaceholder,
                postCodeButton
              );
            });
        }
      } else {
        handleErrorResult(
          provinceSelect,
          provinceCustomSelect,
          provincePlaceholder,
          provinceCustomPlaceholder,
          postCodeButton
        );
        handleErrorResult(
          townSelect,
          townCustomSelect,
          townPlaceholder,
          townCustomPlaceholder,
          postCodeButton
        );
        handleErrorResult(
          countySelect,
          countyCustomSelect,
          countyPlaceholder,
          countyCustomPlaceholder,
          postCodeButton
        );
      }
    });
  }
}

/**
 * Validate the zipcode after clicking on "Find" button to retrieve addresses. If the addresses response is empty, return an error validation
 * on the field showing a message to the user
 */
function validateZipCodeAddress(inputPostCode: HTMLInputElement) {
  const errorList = inputPostCode?.dataset['validation'];

  const hasValidationError = errorList?.includes(STATUS_MATCH_ADDRESS_VALIDATION);
  const zipCodeFieldElement = inputPostCode?.closest('.cmp-form-text--zipcode');

  if (hasValidationError && zipCodeFieldElement) {
    vrValidZipcodeAddress(zipCodeFieldElement, STATUS_MATCH_ADDRESS_VALIDATION, inputPostCode.id);
  }
}

export function clearSelectZipcode(formContainer: HTMLFormElement) {
  //NATIVE
  const provinceSelect = formContainer?.querySelector(
    `select[name="customer.address.province"]`
  ) as HTMLSelectElement;
  const townSelect = formContainer?.querySelector(
    `select[name="customer.address.town"]`
  ) as HTMLSelectElement;
  const countySelect = formContainer?.querySelector(
    `select[name="customer.address.region"]`
  ) as HTMLSelectElement;

  const provincePlaceholder = provinceSelect ? provinceSelect.getAttribute('placeholder') : null;
  const townPlaceholder = townSelect ? townSelect.getAttribute('placeholder') : null;
  const countyPlaceholder = countySelect ? countySelect.getAttribute('placeholder') : null;

  //CUSTOM
  const provinceCustomSelect = formContainer?.querySelector(
    `.custom-select[name="customer.address.province"]`
  ) as HTMLElement;
  const provinceCustomPlaceholder = provinceCustomSelect
    ?.querySelector('.selected-value')
    ?.getAttribute('placeholder') as string;
  const townCustomSelect = formContainer?.querySelector(
    `.custom-select[name="customer.address.town"]`
  ) as HTMLElement;
  const townCustomPlaceholder = townCustomSelect
    ?.querySelector('.selected-value')
    ?.getAttribute('placeholder') as string;
  const countyCustomSelect = formContainer?.querySelector(
    `.custom-select[name="customer.address.region"]`
  ) as HTMLElement;
  const countyCustomPlaceholder = countyCustomSelect
    ?.querySelector('.selected-value')
    ?.getAttribute('placeholder') as string;

  const selects = [
    { select: provinceSelect, placeholder: provincePlaceholder },
    { select: townSelect, placeholder: townPlaceholder },
    { select: countySelect, placeholder: countyPlaceholder },
  ];

  const customSelects = [
    { select: provinceCustomSelect, placeholder: provinceCustomPlaceholder },
    { select: townCustomSelect, placeholder: townCustomPlaceholder },
    { select: countyCustomSelect, placeholder: countyCustomPlaceholder },
  ];

  selects?.forEach(({ select, placeholder }) => {
    if (select) {
      disableSelect(select);
      select.selectedIndex = 0;
      select.options[0].textContent = placeholder;
    }
  });
  customSelects?.forEach(({ select, placeholder }) => {
    if (select) {
      disableSelect(select);
      const selectedValue = select.querySelector('.selected-value');
      if (selectedValue) {
        selectedValue.textContent = placeholder;
      }
    }
  });
  const hiddenProvinceInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.province"][type="hidden"]'
  );
  if (hiddenProvinceInput) {
    hiddenProvinceInput.value = '';
  }

  const hiddenCountyInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.region"][type="hidden"]'
  );
  if (hiddenCountyInput) {
    hiddenCountyInput.value = '';
  }
  const hiddenCountryInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.countryCode"][type="hidden"]'
  );
  if (hiddenCountryInput) {
    hiddenCountryInput.value = '';
  }
}
function checkPropertyConsistency(addresses: any[], property: string) {
  const values = addresses
    .map((address) => address[property])
    .filter((value) => value !== undefined && value !== null && value !== '');

  const firstValue = values[0];
  const consistent = values.every((value) => value === firstValue);

  const differentValues = [...new Set(values)];

  return {
    consistent,
    differentValues,
  };
}

function handleImpactedSelect(
  currentSelect: HTMLSelectElement,
  currentResult: { consistent?: boolean; differentValues: string[] }
) {
  const newOption = document.createElement('option');
  currentSelect?.replaceChildren();
  newOption.value = currentResult.differentValues[0];
  newOption.text = currentResult.differentValues[0];
  currentSelect?.add(newOption);
  currentSelect.disabled = false;
  currentSelect?.closest('.options')?.classList.remove('disabled-select');
  validateSelect(currentSelect);
}
function handleImpactedCustomSelect(
  currentCustomSelect: HTMLElement,
  currentResult: { consistent?: boolean; differentValues: string[] }
) {
  //remove the previous dropdown
  const currentUlElement = currentCustomSelect?.querySelector('ul');
  if (currentUlElement) {
    currentCustomSelect?.removeChild(currentUlElement);
  }
  const newUlElement = document.createElement('ul');
  newUlElement.classList.add('select-dropdown');
  newUlElement.setAttribute('role', 'listbox');
  newUlElement.id = `${currentCustomSelect.getAttribute('name')}`;

  const newLiOption = document.createElement('li');
  newLiOption.setAttribute('role', 'option');

  const newInput = document.createElement('input');
  newInput.type = 'radio';
  newInput.id = `1-${currentCustomSelect.getAttribute('name')}`;
  newInput.name = `${currentCustomSelect.getAttribute('name')}`;
  newInput.value = currentResult.differentValues[0];

  const newLabel = document.createElement('label');
  newLabel.setAttribute('for', newInput.id);
  newLabel.textContent = currentResult.differentValues[0];

  newLiOption.appendChild(newInput);
  newLiOption.appendChild(newLabel);
  newUlElement.appendChild(newLiOption);

  currentCustomSelect.appendChild(newUlElement);

  newLiOption.addEventListener('click', () => {
    const allOptions = newUlElement.querySelectorAll('li');
    allOptions.forEach((option) => option.classList.remove('selected'));
    newLiOption.classList.add('selected');
    const selectedValue = currentResult.differentValues[0];

    const parentDiv = newLiOption.closest('.custom-select');
    const spanElement = parentDiv?.querySelector('.select-button .selected-value');
    if (spanElement) {
      spanElement.textContent = selectedValue;
    }
    const currentSelect = newUlElement.closest('.custom-select');
    currentSelect?.classList.remove('active');

    validateSelectCustom(currentSelect as HTMLElement);
  });

  currentCustomSelect?.closest('.options')?.classList.remove('disabled-select');
}
function handleImpactedSelectMultiValues(
  currentSelect: HTMLSelectElement,
  currentResult: { consistent?: boolean; differentValues: string[] }
) {
  currentSelect?.replaceChildren();
  currentResult.differentValues.forEach((currentValue: string) => {
    const newOption = document.createElement('option');
    newOption.value = currentValue;
    newOption.text = currentValue;
    currentSelect.appendChild(newOption);
    currentSelect?.closest('.options')?.classList.remove('disabled-select');
  });
  currentSelect.disabled = false;
  currentSelect?.closest('.options')?.classList.remove('disabled-select');
  validateSelect(currentSelect);
}

/**
 * Handles the impacted custom select element by updating the available options
 * based on the provided result set. It dynamically creates and updates a dropdown list
 * with selectable options and applies corresponding event listeners for interaction.
 *
 * @param {HTMLElement} currentCustomSelect - The custom select HTML element where the options will be updated.
 * @param {{consistent?: boolean, differentValues: string[]}} currentResult - An object containing a flag that indicates consistency and an array of different values to populate the dropdown.
 * @return {void} - This method updates the DOM but does not return a value.
 */
function handleImpactedCustomSelectMultiValues(
  currentCustomSelect: HTMLElement,
  currentResult: { consistent?: boolean; differentValues: string[] }
) {
  currentCustomSelect?.querySelector('ul')?.remove();

  const newUlElement = document.createElement('ul');
  newUlElement.classList.add('select-dropdown');
  newUlElement.setAttribute('role', 'listbox');
  newUlElement.id = `${currentCustomSelect.getAttribute('name')}`;

  currentResult.differentValues.forEach((currentValue: string, index: number) => {
    const newLiOption = document.createElement('li');
    newLiOption.setAttribute('role', 'option');

    const newInput = document.createElement('input');
    newInput.type = 'radio';
    newInput.id = `${index + 1}-${currentCustomSelect.getAttribute('name')}`;
    newInput.name = `${currentCustomSelect.getAttribute('name')}`;
    newInput.value = currentValue;

    const newLabel = document.createElement('label');
    newLabel.setAttribute('for', newInput.id);
    newLabel.textContent = currentValue;

    newLiOption.appendChild(newInput);
    newLiOption.appendChild(newLabel);
    newUlElement.appendChild(newLiOption);

    newLiOption.addEventListener('click', () => {
      const allOptions = newUlElement.querySelectorAll('li');
      allOptions.forEach((option) => option.classList.remove('selected'));
      newLiOption.classList.add('selected');

      const spanElement = currentCustomSelect.querySelector('.select-button .selected-value');
      if (spanElement) {
        spanElement.textContent = currentValue;
      }

      currentCustomSelect.classList.remove('active');
      validateSelectCustom(currentCustomSelect);
    });
  });

  currentCustomSelect.appendChild(newUlElement);
  currentCustomSelect?.closest('.options')?.classList.remove('disabled-select');
}
function handleImpactedPlaceholderSelect(
  currentSelect: HTMLSelectElement,
  currentPlaceholder: HTMLOptionElement
) {
  currentSelect?.replaceChildren();
  currentSelect?.add(currentPlaceholder);
  currentSelect.disabled = true;
  currentSelect?.closest('.options')?.classList.add('disabled-select');
}
function handleImpactedCustomPlaceholderSelect(
  currentCustomSelect: HTMLElement,
  currentPlaceholder: string
) {
  currentCustomSelect?.querySelector('ul')?.remove();
  const spanElement = currentCustomSelect?.querySelector('span');
  if (spanElement) {
    spanElement.textContent = currentPlaceholder;
  }
  currentCustomSelect?.closest('.options')?.classList.add('disabled-select');
}

function handleErrorResult(
  nativeSelect: HTMLSelectElement,
  customSelect: HTMLElement,
  nativePlaceholder: HTMLOptionElement,
  customPlaceholder: string,
  postCodeButton: HTMLButtonElement
) {
  if (nativeSelect) {
    handleImpactedPlaceholderSelect(nativeSelect, nativePlaceholder);
  } else if (customSelect) {
    handleImpactedCustomPlaceholderSelect(customSelect, customPlaceholder);
  }
  postCodeButton!.disabled = false;

  const hiddenProvinceInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.province"][type="hidden"]'
  );
  if (hiddenProvinceInput) {
    hiddenProvinceInput.value = '';
  }

  const hiddenCountyInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.region"][type="hidden"]'
  );
  if (hiddenCountyInput) {
    hiddenCountyInput.value = '';
  }

  const hiddenCountryCodeInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.countryCode"][type="hidden"]'
  );
  if (hiddenCountryCodeInput) {
    hiddenCountryCodeInput.value = '';
  }
}

function handleConsistencyResult(
  property: string,
  responseAddresses: any[],
  nativeSelect: HTMLSelectElement | null,
  customSelect: HTMLElement,
  placeholder: HTMLOptionElement,
  customPlaceholder: string
) {
  const result = checkPropertyConsistency(responseAddresses, property);
  const hiddenProvinceInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.province"][type="hidden"]'
  );
  const hiddenCountyInput = document.querySelector<HTMLInputElement>(
    'input[name="customer.address.region"][type="hidden"]'
  );

  if (result.differentValues.length === 0) {
    if (nativeSelect) {
      handleImpactedPlaceholderSelect(nativeSelect, placeholder);
    } else if (customSelect) {
      handleImpactedCustomPlaceholderSelect(customSelect, customPlaceholder);
    }
    if (hiddenProvinceInput && property === 'province') {
      hiddenProvinceInput.value = '';
    }
    if (hiddenCountyInput && property === 'region') {
      hiddenCountyInput.value = '';
    }
    return;
  }

  if (result.consistent) {
    if (nativeSelect) {
      handleImpactedSelect(nativeSelect, result);
    } else if (customSelect) {
      handleImpactedCustomSelect(customSelect, result);
    }
    if (hiddenProvinceInput && property === 'province') {
      hiddenProvinceInput.value = result.differentValues[0];
    }
    if (hiddenCountyInput && property === 'region') {
      hiddenCountyInput.value = result.differentValues[0];
    }
  } else {
    if (nativeSelect) {
      handleImpactedSelectMultiValues(nativeSelect, result);
    } else if (customSelect) {
      handleImpactedCustomSelectMultiValues(customSelect, result);
    }
    if (hiddenProvinceInput && property === 'province') {
      hiddenProvinceInput.value = result.differentValues[0];
    }
    if (hiddenCountyInput && property === 'region') {
      hiddenCountyInput.value = result.differentValues[0];
    }
  }
}

/**
 * Disables a select element by applying a CSS class under specific conditions.
 *
 * @param {HTMLElement} select - The select element to be analyzed and potentially disabled.
 * @return {void} This function does not return a value.
 */
function disableSelect(select: HTMLElement) {
  if (select instanceof HTMLSelectElement) {
    const isCountryCode = select.name?.endsWith('address.countryCode');
    const hasEmptyOption = select.querySelector('option[value=""]') !== null;

    if (isCountryCode && !hasEmptyOption) {
      return;
    }

    const options = select?.closest('.options');
    options?.classList.add('disabled-select');
  }
}

document.querySelectorAll(`input[name="customer.address.postCode"]`).forEach((node) => {
  new InputPostcode(node as HTMLInputElement).init();
});
